import React, { useCallback, useEffect, useState } from 'react';
import '../../styled/pages/dashboard.scss';
import { Icon } from '@ohif/ui';
import {
  CustomTable,
  Header,
  Loader,
  ConfirmationModal,
  Success,
  Failed,
} from '../../components';
import { customStyles } from '../../Utils/utils';
import { getToken } from '../../Utils/authUtils';
import { USERS } from '../../constant';

interface GpAccount {
  _id: string;
  name: string;
  email: string;
  providerNumber: string;
  status?: string;
  createdOn?: string;
}

const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

const columns = [
  { key: 'providerNumber', title: 'Provider Number' },
  { key: 'name', title: 'Name' },
  { key: 'email', title: 'Email' },
  { key: 'statusBadge', title: 'Status' },
];

const GpAccountsAdmin = () => {
  const [accounts, setAccounts] = useState<GpAccount[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [form, setForm] = useState({ providerNumber: '', name: '', email: '', password: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [resetTarget, setResetTarget] = useState<GpAccount | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetting, setResetting] = useState(false);

  const [confirmTarget, setConfirmTarget] = useState<GpAccount | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [isFailedOpen, setIsFailedOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(USERS.GP_ACCOUNTS, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || `Failed: ${res.status}`);
      const list: GpAccount[] = data?.result || [];
      setAccounts(list);
      setTableData(
        list.map(acc => ({
          ...acc,
          statusBadge: (acc.status || 'Active') === 'Active' ? 'Active' : 'Inactive',
        }))
      );
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to load GP accounts');
      setIsFailedOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredData = tableData.filter(acc =>
    !searchTerm ||
    acc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.providerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    acc.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const { providerNumber, name, email, password } = form;
    if (!providerNumber.trim() || !name.trim() || !email.trim() || !password) {
      setFormError('All fields are required');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const res = await fetch(USERS.GP_ACCOUNTS, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          providerNumber: providerNumber.trim(),
          name: name.trim(),
          email: email.trim(),
          password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || `Failed: ${res.status}`);
      setIsAddOpen(false);
      setForm({ providerNumber: '', name: '', email: '', password: '' });
      setSuccessMsg(`GP account created for ${providerNumber.trim()}`);
      setIsSuccessOpen(true);
      load();
    } catch (err: any) {
      setFormError(err?.message || 'Failed to create GP account');
    } finally {
      setSaving(false);
    }
  };

  const confirmToggle = (acc: GpAccount) => {
    setConfirmTarget(acc);
    setIsConfirmOpen(true);
  };

  const executeToggle = async () => {
    if (!confirmTarget) return;
    const next = (confirmTarget.status || 'Active') === 'Active' ? 'Inactive' : 'Active';
    setIsConfirmOpen(false);
    try {
      const res = await fetch(USERS.GP_ACCOUNT_STATUS(confirmTarget._id), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || `Failed: ${res.status}`);
      setSuccessMsg(`${confirmTarget.providerNumber} ${next === 'Active' ? 'enabled' : 'disabled'}`);
      setIsSuccessOpen(true);
      load();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to update status');
      setIsFailedOpen(true);
    } finally {
      setConfirmTarget(null);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetTarget) return;
    if (resetPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }
    setResetting(true);
    setResetError('');
    try {
      const res = await fetch(USERS.GP_ACCOUNT_RESET_PASSWORD(resetTarget._id), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ password: resetPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.msg || `Failed: ${res.status}`);
      setSuccessMsg(`Password reset for ${resetTarget.providerNumber}`);
      setIsSuccessOpen(true);
      setResetTarget(null);
      setResetPassword('');
    } catch (err: any) {
      setResetError(err?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const actions = [
    {
      label: 'Reset Password',
      iconName: 'edit-icon',
      onClick: (item: any) => {
        const acc = accounts.find(a => a._id === item._id);
        if (acc) { setResetTarget(acc); setResetPassword(''); setResetError(''); }
      },
    },
    {
      label: 'Toggle Status',
      iconName: 'delete-icon',
      onClick: (item: any) => {
        const acc = accounts.find(a => a._id === item._id);
        if (acc) confirmToggle(acc);
      },
    },
  ];

  return (
    <section className="layoutContainer" style={customStyles.layoutBackground}>
      <div className="layoutGrid">
        <div className="page page--fullBody">
          <Header />
          <div className="page__body">
            <div className="page__content">
              <div className="page__body__head">
                <h1 className="title title--h6 title--normal">GP Portal Accounts</h1>
                <div className="btn__group">
                  <button
                    type="button"
                    className="btn btn__primary"
                    onClick={() => { setIsAddOpen(true); setFormError(''); }}
                  >
                    <Icon name="add-icon" />
                    Add GP Account
                  </button>
                </div>
              </div>

              <div className="tableWrap">
                <div className="tableWrap__head">
                  <div className="formRow">
                    <div className="formCol relative">
                      <input
                        className="form__input form__input--search"
                        type="text"
                        placeholder="Search by name, provider number or email"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && (
                        <span className="absolute right-8 bottom-2 cursor-pointer" onClick={() => setSearchTerm('')}>
                          <Icon name="clear-icon" />
                        </span>
                      )}
                      <span className="absolute right-4 bottom-3 cursor-pointer">
                        <Icon name="search-icon" />
                      </span>
                    </div>
                  </div>
                </div>
                <CustomTable
                  columns={columns}
                  tableData={filteredData}
                  actions={actions}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Loader isLoading={loading} />

      {/* Add Account Modal */}
      {isAddOpen && (
        <div className="modal modal--open">
          <div className="modal__dialog" style={{ maxWidth: '480px' }}>
            <div
              className="modal__content"
              style={{
                width: '100%',
                background: 'rgba(12,26,46,0.97)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '14px',
                padding: 0,
                gap: 0,
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '18px 24px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div>
                  <h1 style={{ fontSize: '17px', fontWeight: 600, color: '#fff', margin: 0 }}>Add GP Account</h1>
                  <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.45)', margin: '2px 0 0' }}>
                    The GP will sign in with their provider number
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddOpen(false)}
                  aria-label="Close"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: 'none',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreate} style={{ width: '100%' }}>
                {/* Body */}
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                  {[
                    { key: 'providerNumber', label: 'Provider Number', placeholder: 'e.g. 0410696F', type: 'text' },
                    { key: 'name', label: 'GP Name', placeholder: 'Dr Jane Smith', type: 'text' },
                    { key: 'email', label: 'Email', placeholder: 'gp@example.com', type: 'email' },
                    { key: 'password', label: 'Initial Password', placeholder: 'Minimum 8 characters', type: 'text' },
                  ].map(f => (
                    <div key={f.key} style={{ width: '100%' }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: '12px',
                          fontWeight: 600,
                          letterSpacing: '0.04em',
                          color: 'rgba(255,255,255,0.6)',
                          marginBottom: '6px',
                        }}
                      >
                        {f.label}
                      </label>
                      <input
                        type={f.type}
                        value={(form as any)[f.key]}
                        placeholder={f.placeholder}
                        onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        style={{
                          width: '100%',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.12)',
                          borderRadius: '8px',
                          padding: '10px 14px',
                          fontSize: '14px',
                          color: '#fff',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                        onFocus={e => (e.target.style.borderColor = '#1ea9ea')}
                        onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                      />
                      {f.key === 'password' && (
                        <p style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.35)', margin: '5px 0 0' }}>
                          Share this securely with the GP — they can use it on first login.
                        </p>
                      )}
                    </div>
                  ))}

                  {formError && (
                    <div
                      style={{
                        background: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.3)',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        color: '#f87171',
                      }}
                    >
                      {formError}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    padding: '16px 24px',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    width: '100%',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn__white btn__outlined"
                    onClick={() => setIsAddOpen(false)}
                    style={{ borderRadius: '8px' }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn__primary" disabled={saving} style={{ borderRadius: '8px' }}>
                    {saving ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal__backdrop" aria-hidden />
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="modal modal--open">
          <div className="modal__dialog" style={{ maxWidth: '440px' }}>
            <div
              className="modal__content"
              style={{
                width: '100%',
                background: 'rgba(12,26,46,0.97)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '14px',
                padding: 0,
                gap: 0,
                boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '18px 24px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div>
                  <h1 style={{ fontSize: '17px', fontWeight: 600, color: '#fff', margin: 0 }}>Reset Password</h1>
                  <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.45)', margin: '2px 0 0' }}>
                    {resetTarget.name} · {resetTarget.providerNumber}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setResetTarget(null)}
                  aria-label="Close"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: 'none',
                    borderRadius: '8px',
                    width: '32px',
                    height: '32px',
                    color: 'rgba(255,255,255,0.7)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleReset} style={{ width: '100%' }}>
                {/* Body */}
                <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                  <div style={{ width: '100%' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        color: 'rgba(255,255,255,0.6)',
                        marginBottom: '6px',
                      }}
                    >
                      New Password
                    </label>
                    <input
                      type="text"
                      value={resetPassword}
                      autoFocus
                      placeholder="Minimum 8 characters"
                      onChange={e => setResetPassword(e.target.value)}
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '14px',
                        color: '#fff',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                      onFocus={e => (e.target.style.borderColor = '#1ea9ea')}
                      onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.12)')}
                    />
                    <p style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.35)', margin: '5px 0 0' }}>
                      Share this securely with the GP.
                    </p>
                  </div>

                  {resetError && (
                    <div
                      style={{
                        background: 'rgba(248,113,113,0.08)',
                        border: '1px solid rgba(248,113,113,0.3)',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        fontSize: '13px',
                        color: '#f87171',
                      }}
                    >
                      {resetError}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    padding: '16px 24px',
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    width: '100%',
                  }}
                >
                  <button
                    type="button"
                    className="btn btn__white btn__outlined"
                    onClick={() => setResetTarget(null)}
                    style={{ borderRadius: '8px' }}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn__primary" disabled={resetting} style={{ borderRadius: '8px' }}>
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="modal__backdrop" aria-hidden />
        </div>
      )}

      <ConfirmationModal
        title={confirmTarget ? `${(confirmTarget.status || 'Active') === 'Active' ? 'Disable' : 'Enable'} GP Account` : ''}
        description={confirmTarget
          ? `Are you sure you want to ${(confirmTarget.status || 'Active') === 'Active' ? 'disable' : 'enable'} ${confirmTarget.providerNumber}?`
          : ''}
        isConfirmationModalOpen={isConfirmOpen}
        handleCloseModal={() => { setIsConfirmOpen(false); setConfirmTarget(null); }}
        handleConfirm={executeToggle}
        cancelActionText="Cancel"
        confirmActionText="Yes"
        isPatientReport={true}
      />

      <Success
        title={successMsg}
        isSuccessModalOpen={isSuccessOpen}
        handleCloseModal={() => setIsSuccessOpen(false)}
      />
      <Failed
        title={errorMsg}
        isFailedModalOpen={isFailedOpen}
        handleCloseModal={() => setIsFailedOpen(false)}
      />
    </section>
  );
};

export default GpAccountsAdmin;
